import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useT } from "@/i18n/LanguageContext";

// Factory "⏰ Need More Time": extra days + reason → approval request to
// Super Admin + Factory One administrators. Shows the pending / approved state.
export function ExtensionRequestButton({ order, size = "sm", className = "" }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState("3");
  const [reason, setReason] = useState("");
  const req = order?.production_extension_request;
  const pending = req?.status === "pending";
  const submit = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/production-extension`, { days: Number(days), reason: reason.trim() }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(t("Request sent — awaiting approval")); setOpen(false); setReason(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <>
      <Button size={size} data-testid={`ext-request-${order.id}`} disabled={pending} onClick={() => setOpen(true)}
              className={`bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-semibold ${className}`}
              title={pending ? t("Extension requested") : t("Need More Time")}>
        ⏰ {pending ? `${t("Extension requested")} (+${req.days})` : t("Need More Time")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="ext-request-dialog">
          <DialogHeader><DialogTitle>⏰ {t("Need More Time")} · {order.po_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">{t("Super Admin and the Factory One Administrator will be asked to approve. Once approved, the 7-day deadline is extended by these days and HQ2 is notified.")}</p>
            <div>
              <Label className="text-xs">{t("Extra days")}</Label>
              <Input data-testid="ext-days" type="number" min="1" max="60" value={days} onChange={(e) => setDays(e.target.value)} className="font-mono" />
            </div>
            <div>
              <Label className="text-xs">{t("Reason")}</Label>
              <Textarea data-testid="ext-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("Why is more time needed?")} />
            </div>
            <Button data-testid="ext-submit" disabled={!reason.trim() || !(Number(days) >= 1) || submit.isPending} onClick={() => submit.mutate()}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900">
              ⏰ {t("Request more time")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function canDecideExtension(user) {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  return String(user.role || "").startsWith("factory_") && (user.sub_role || "administrator") === "administrator";
}

export function ExtensionDecideButtons({ orderId, size = "sm" }) {
  const { t } = useT();
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: (action) => api.post(`/orders/${orderId}/production-extension/${action}`),
    onSuccess: (r, action) => {
      qc.invalidateQueries();
      toast.success(action === "approve" ? `${t("Approved")} · ${t("Deadline")}: ${(r?.data?.deadline || "").slice(0, 10)}` : t("Rejected"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <>
      <Button size={size} data-testid={`ext-approve-${orderId}`} disabled={decide.isPending} onClick={() => decide.mutate("approve")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">✓ {t("Approve")}</Button>
      <Button size={size} data-testid={`ext-reject-${orderId}`} disabled={decide.isPending} onClick={() => decide.mutate("reject")}
              variant="outline" className="border-red-400 text-red-700 hover:bg-red-50">✕ {t("Reject")}</Button>
    </>
  );
}
